import {
  buildExplorerTree,
  isPathPrefix,
  joinChildPath,
  joinUnderParent,
  leafSegment,
  listAllFolderPaths,
  parentPathOf,
  type PathItem
} from './tree'

export type ExplorerClipboardSource =
  | { kind: 'folder'; path: string; name: string }
  | { kind: 'item'; id: string; path: string; name: string }

export type ExplorerClipboardEntry = ExplorerClipboardSource & {
  mode: 'copy' | 'cut'
}

export const PASTE_REFUSED = {
  stale: 'The item you cut is no longer available.',
  locked: 'This item cannot be copied or moved.',
  lockedDest: 'Cannot paste into this folder.',
  cycle: 'Cannot paste a folder into itself or a descendant.',
  tooMany: 'Too many copies in this folder. Rename one of them first.'
} as const

export type PasteResult =
  | { kind: 'noop' }
  | { kind: 'refused'; reason: string }
  | { kind: 'move'; type: 'folder'; fromPath: string; toParentPath: string }
  | { kind: 'move'; type: 'item'; id: string; toParentPath: string }
  | { kind: 'duplicate'; type: 'folder'; fromPath: string; name: string; toPath: string }
  | { kind: 'duplicate'; type: 'item'; id: string; name: string; path: string }

export function explorerModShortcut(key: string): string {
  if (typeof navigator !== 'undefined' && !/Mac|iPhone|iPad/.test(navigator.platform)) {
    return `Ctrl+${key}`
  }
  return `⌘${key}`
}

export function hasTextSelection(): boolean {
  return (window.getSelection()?.toString() ?? '') !== ''
}

export function takenExplorerPaths<F extends PathItem, I extends PathItem>(
  folders: readonly F[],
  items: readonly I[]
): Set<string> {
  const taken = new Set(listAllFolderPaths(buildExplorerTree(folders, items)))
  taken.delete('')
  for (const item of items) taken.add(item.path)
  return taken
}

export function nextAvailableCopy(
  base: { name: string; leaf: string },
  destPath: string,
  takenPaths: ReadonlySet<string>
): { name: string; path: string } {
  const asIs = joinUnderParent(destPath, base.leaf)
  if (asIs !== '' && !takenPaths.has(asIs)) return { name: base.name, path: asIs }
  for (let n = 2; n <= 99; n += 1) {
    const name = `${base.name} (${n})`
    const path = joinChildPath(destPath, name)
    if (path !== '' && !takenPaths.has(path)) return { name, path }
  }
  throw new Error(PASTE_REFUSED.tooMany)
}

export function folderStillExists<F extends PathItem, I extends PathItem>(
  path: string,
  folders: readonly F[],
  items: readonly I[]
): boolean {
  return (
    folders.some((folder) => folder.path === path || isPathPrefix(path, folder.path)) ||
    items.some((item) => isPathPrefix(path, item.path) && item.path !== path)
  )
}

export function resolvePasteTarget<F extends PathItem, I extends PathItem>(
  entry: ExplorerClipboardEntry | null,
  destPath: string,
  folders: readonly F[],
  items: readonly I[],
  locks?: {
    isLockedPath?: (path: string) => boolean
  }
): PasteResult {
  if (entry === null) return { kind: 'noop' }
  if (locks?.isLockedPath?.(destPath) === true) {
    return { kind: 'refused', reason: PASTE_REFUSED.lockedDest }
  }

  if (entry.kind === 'folder') {
    if (locks?.isLockedPath?.(entry.path) === true) {
      return { kind: 'refused', reason: PASTE_REFUSED.locked }
    }
    if (!folderStillExists(entry.path, folders, items)) {
      return { kind: 'refused', reason: PASTE_REFUSED.stale }
    }
    if (isPathPrefix(entry.path, destPath)) {
      return { kind: 'refused', reason: PASTE_REFUSED.cycle }
    }
    if (entry.mode === 'cut') {
      const toPath = joinUnderParent(destPath, leafSegment(entry.path))
      if (toPath === entry.path) return { kind: 'noop' }
      return { kind: 'move', type: 'folder', fromPath: entry.path, toParentPath: destPath }
    }
    try {
      const taken = takenExplorerPaths(folders, items)
      const copy = nextAvailableCopy(
        { name: entry.name, leaf: leafSegment(entry.path) },
        destPath,
        taken
      )
      return {
        kind: 'duplicate',
        type: 'folder',
        fromPath: entry.path,
        name: copy.name,
        toPath: copy.path
      }
    } catch (err) {
      return { kind: 'refused', reason: err instanceof Error ? err.message : PASTE_REFUSED.tooMany }
    }
  }

  if (locks?.isLockedPath?.(entry.path) === true) {
    return { kind: 'refused', reason: PASTE_REFUSED.locked }
  }
  const item = items.find((row) => row.id === entry.id)
  if (item === undefined) {
    return { kind: 'refused', reason: PASTE_REFUSED.stale }
  }
  if (entry.mode === 'cut') {
    const toPath = joinUnderParent(destPath, leafSegment(item.path))
    if (toPath === item.path) return { kind: 'noop' }
    return { kind: 'move', type: 'item', id: item.id, toParentPath: destPath }
  }
  try {
    const taken = takenExplorerPaths(folders, items)
    const copy = nextAvailableCopy(
      { name: item.name, leaf: leafSegment(item.path) },
      destPath,
      taken
    )
    return { kind: 'duplicate', type: 'item', id: item.id, name: copy.name, path: copy.path }
  } catch (err) {
    return { kind: 'refused', reason: err instanceof Error ? err.message : PASTE_REFUSED.tooMany }
  }
}

/** Parent folder a new/pasted row should land in, given the current selection. */
export function pasteDestinationPath(
  selection: { kind: 'folder'; path: string } | { kind: 'item'; path: string } | { kind: 'none' },
  isLockedPath?: (path: string) => boolean
): string {
  if (selection.kind === 'folder') {
    return isLockedPath?.(selection.path) === true ? '' : selection.path
  }
  if (selection.kind === 'item') {
    const parent = parentPathOf(selection.path)
    return isLockedPath?.(parent) === true ? '' : parent
  }
  return ''
}
